import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminFetch, AdminAuthError } from '../lib/adminApi';
import '../styles/admin-premios.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://giroexpress-ruleta-backend.onrender.com';

const FILTERS = [
  { key: 'todos', label: 'Todos' },
  { key: 'available', label: 'Disponibles' },
  { key: 'pending', label: 'Pago pendiente' },
  { key: 'correction_required', label: 'Corrección' },
  { key: 'approved', label: 'Aprobados' },
  { key: 'delivered', label: 'Entregados' },
  { key: 'applied', label: 'Mensajes' },
];

const STATUS_MAP = {
  available: { label: 'Disponible', cls: 'st-pending' },
  pending: { label: 'Pago pendiente', cls: 'st-claimed' },
  claimed: { label: 'Reclamado', cls: 'st-claimed' },
  delivered: { label: 'Entregado', cls: 'st-delivered' },
  approved: { label: 'Aprobado', cls: 'st-delivered' },
  correction_required: { label: 'Corrección', cls: 'st-correction' },
  applied: { label: 'Registrado', cls: 'st-delivered' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][d.getMonth()];
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd} ${mm} ${hh}:${mi}`;
}

// Etiqueta visible del método de retiro
const WITHDRAWAL_LABELS = {
  pago_movil: 'Pago Móvil',
  bancolombia: 'Bancolombia',
  nequi: 'Nequi',
};

// Devuelve el método guardado en claim_data. Fallback "pago_movil" SOLO para
// premios antiguos que nunca recibieron withdrawal_method.
function getWithdrawalMethod(cd) {
  if (!cd) return 'pago_movil';
  const m = (cd.withdrawal_method || '').toLowerCase();
  if (m === 'bancolombia' || m === 'nequi' || m === 'pago_movil') return m;
  return 'pago_movil';
}

// Render compacto de los datos según el método
function renderClaimDataLines(cd) {
  if (!cd) return null;
  const method = getWithdrawalMethod(cd);
  if (method === 'bancolombia') {
    const tipo = cd.tipo_cuenta === 'corriente' ? 'Corriente' : 'Ahorros';
    return (
      <>
        <span className="pago-line">Cta. {cd.numero_cuenta || '—'} · {tipo}</span>
        <span className="pago-line pago-sub">{cd.cedula || '—'} · {cd.nombre_titular || '—'}</span>
      </>
    );
  }
  if (method === 'nequi') {
    return (
      <>
        <span className="pago-line">Nequi {cd.numero_nequi || '—'}</span>
        <span className="pago-line pago-sub">{cd.cedula || '—'} · {cd.nombre_titular || '—'}</span>
      </>
    );
  }
  // pago_movil (incluye legacy sin withdrawal_method)
  return (
    <>
      <span className="pago-line">{cd.banco || '—'} · {cd.telefono || '—'}</span>
      <span className="pago-line pago-sub">{cd.cedula || '—'} · {cd.nombre_titular || '—'}</span>
    </>
  );
}

export default function AdminPremios() {
  const navigate = useNavigate();
  const [premios, setPremios] = useState([]);
  const [count, setCount] = useState(0);
  const [filter, setFilter] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Correction modal
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [correctionMessage, setCorrectionMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchPremios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = filter !== 'todos' ? `?status=${filter}` : '';
      const res = await adminFetch(`${API_URL}/api/admin/premios${qs}`);
      if (res.status === 401 || res.status === 403) {
        setError(res.status === 401
          ? 'Sesión inválida. Inicia sesión como administrador.'
          : 'No tienes permisos de administrador.');
        setPremios([]);
        setCount(0);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setPremios(data.premios);
        setCount(data.count);
      } else {
        setError(data.error || 'Error desconocido');
      }
    } catch (e) {
      if (e instanceof AdminAuthError) {
        setError(e.message);
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchPremios(); }, [fetchPremios]);

  const sts = (s) => STATUS_MAP[s] || { label: s || '—', cls: '' };

  const openCorrectionModal = (premio) => {
    setCorrectionTarget(premio);
    setCorrectionMessage('');
    setShowCorrectionModal(true);
  };

  const submitCorrection = async () => {
    if (!correctionTarget || !correctionMessage.trim()) return;
    setSubmitting(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/premios/${correctionTarget.prize_id}/correction`, {
        method: 'POST',
        body: JSON.stringify({ message: correctionMessage.trim() })
      });
      if (res.status === 401 || res.status === 403) {
        alert(res.status === 401 ? 'Sesión inválida. Inicia sesión como administrador.' : 'No tienes permisos de administrador.');
        return;
      }
      const data = await res.json();
      if (data.success) {
        setShowCorrectionModal(false);
        fetchPremios();
      }
    } catch (e) {
      if (e instanceof AdminAuthError) alert(e.message);
      else console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const approvePrize = async (premio) => {
    if (!premio || submitting) return;
    if (!window.confirm(`¿Confirmas el pago y entrega de "${premio.title}"?`)) return;
    setSubmitting(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/premios/${premio.prize_id}/approve`, {
        method: 'POST'
      });
      if (res.status === 401 || res.status === 403) {
        alert(res.status === 401 ? 'Sesión inválida. Inicia sesión como administrador.' : 'No tienes permisos de administrador.');
        return;
      }
      const data = await res.json();
      if (data.success) {
        fetchPremios();
      }
    } catch (e) {
      if (e instanceof AdminAuthError) alert(e.message);
      else console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="adm-container" data-testid="admin-premios-page">
      <header className="adm-header">
        <button className="adm-back" onClick={() => navigate('/')} data-testid="admin-back-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18L9 12L15 6"/></svg>
        </button>
        <h1 className="adm-title">Premios ganados</h1>
        <button className="adm-refresh" onClick={fetchPremios} data-testid="admin-refresh-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 4v6h6"/><path d="M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/></svg>
        </button>
      </header>

      <div className="adm-filters" data-testid="admin-filters">
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`adm-filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <p className="adm-count" data-testid="admin-count">{count} resultado{count !== 1 ? 's' : ''}</p>

      {loading && <div className="adm-loading"><div className="adm-spinner" /></div>}

      {error && (
        <div className="adm-error" data-testid="admin-error">
          <p>{error}</p>
          <button onClick={fetchPremios}>Reintentar</button>
        </div>
      )}

      {!loading && !error && premios.length === 0 && (
        <div className="adm-empty" data-testid="admin-empty">
          <p>No hay premios con este filtro.</p>
        </div>
      )}

      {!loading && !error && premios.length > 0 && (
        <div className="adm-table-wrap" data-testid="admin-table">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Usuario</th>
                <th>Email</th>
                <th>Premio</th>
                <th>Monto</th>
                <th>Estado</th>
                <th>Método / Datos de retiro</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {premios.map((p) => {
                const s = sts(p.status);
                const cd = p.claim_data;
                const ch = p.correction_history || [];
                return (
                  <tr key={p.prize_id || p.spin_id} data-testid={`row-${p.prize_id}`}>
                    <td className="col-date">{fmtDate(p.created_at)}</td>
                    <td className="col-user">
                      <span className="user-name">{p.display_name || '—'}</span>
                      <span className="user-ref">{p.user_ref}</span>
                    </td>
                    <td className="col-email">{p.email || '—'}</td>
                    <td className="col-title">{p.title}</td>
                    <td className="col-amount">{p.type === 'money_cop' ? `$${(p.amount || 0).toLocaleString('es-CO')}` : '—'}</td>
                    <td><span className={`adm-badge ${s.cls}`}>{s.label}</span></td>
                    <td className="col-pago">
                      {cd ? (
                        <div className="pago-info">
                          <span className="pago-line pago-method" data-testid={`row-method-${p.prize_id}`}>
                            {WITHDRAWAL_LABELS[getWithdrawalMethod(cd)]}
                          </span>
                          {renderClaimDataLines(cd)}
                          {cd.nota && <span className="pago-line pago-nota">{cd.nota}</span>}
                        </div>
                      ) : (
                        <span className="pago-empty">—</span>
                      )}
                      {ch.length > 0 && (
                        <div className="correction-history">
                          <span className="ch-label">Correcciones: {ch.length}</span>
                        </div>
                      )}
                    </td>
                    <td className="col-actions">
                      {p.type === 'money_cop' && (p.status === 'pending' || p.status === 'claimed') && (
                        <>
                          <button 
                            className="adm-btn-correction"
                            onClick={() => openCorrectionModal(p)}
                            data-testid={`btn-correction-${p.prize_id}`}
                          >
                            Corrección
                          </button>
                          <button 
                            className="adm-btn-correction"
                            style={{ marginLeft: 8, background: '#10b981', color: '#fff' }}
                            onClick={() => approvePrize(p)}
                            data-testid={`btn-approve-${p.prize_id}`}
                          >
                            Aprobar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Corrección */}
      {showCorrectionModal && correctionTarget && (
        <div className="adm-modal-overlay" onClick={() => !submitting && setShowCorrectionModal(false)}>
          <div className="adm-modal" onClick={e => e.stopPropagation()} data-testid="correction-modal">
            <h3 className="adm-modal-title">Solicitar corrección</h3>
            <p className="adm-modal-sub">Premio: <strong>{correctionTarget.title}</strong> — {correctionTarget.display_name}</p>
            
            {correctionTarget.claim_data && (
              <div className="adm-modal-current">
                <span className="adm-modal-label">
                  Método: <strong>{WITHDRAWAL_LABELS[getWithdrawalMethod(correctionTarget.claim_data)]}</strong>
                </span>
                {renderClaimDataLines(correctionTarget.claim_data)}
              </div>
            )}

            <div className="adm-modal-field">
              <label>Motivo de corrección *</label>
              <textarea 
                rows="3"
                placeholder="Ej: El número de teléfono no corresponde al banco seleccionado..."
                value={correctionMessage}
                onChange={e => setCorrectionMessage(e.target.value)}
                data-testid="correction-message-input"
              />
            </div>

            <div className="adm-modal-actions">
              <button className="adm-btn-cancel" onClick={() => setShowCorrectionModal(false)} disabled={submitting}>
                Cancelar
              </button>
              <button 
                className="adm-btn-submit" 
                onClick={submitCorrection} 
                disabled={submitting || !correctionMessage.trim()}
                data-testid="correction-submit"
              >
                {submitting ? 'Enviando...' : 'Enviar corrección'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
